import React, { useCallback, useEffect } from "react";
import { ConnectedProps, connect } from "react-redux";
import { actions as detailTransaction } from "reducers/detailTransaction";
import { TransactionResponseTypeResult } from "sagas/types/transactions";
import { AppDispatch, RootState } from "store";
import "./TableTransaction.scss";
import { TableTransactionPropsTypes } from "./types";
import { formatDDMMYYYY, formatHHMM } from "utils/date";
import "./i18n";
import { useTranslation } from "react-i18next";
import {
  actions as transactionActions,
  allTransactionsSelected,
  transactionsSelector,
} from "reducers/transactions";
import { HeaderType, Table } from "@pickit/pickit-components";
import useTableConfig from "hooks/useTableConfig";
import { TRANSACTION_STATUS } from "constants/transactions";

export const TableTransaction = ({
  transactions,
  getDetailTransaction,
  transactionsSelected,
  isAllSelected,
  testId,
  filters,
  limit,
  offset,
  seeMore,
  collapse,
  getMoreTransactions,
  toggleItem,
  toggleAll,
  setCollapseTable,
}: TableTransactionPropsTypes & ConnectorProps) => {
  const { t } = useTranslation("tableTransactions");
  const { fontSize, rowCount } = useTableConfig(collapse);

  useEffect(() => {
    setCollapseTable(true);
  }, [setCollapseTable]);

  const maxlengthColumns = (data: string): string => {
    return data.length > 10 && window.screen.width < 1920
      ? data.substring(0, 10) + "..."
      : data.length > 12 && window.screen.width >= 1920
        ? data.substring(0, 12) + "..."
        : data;
  };

  const formatNoValue = (value: any): string => {
    return value && value.toString().trim() !== '' ? value.toString() : '-';
  };

  const isSelected = useCallback(
    (itemId: number) =>
      transactionsSelected?.map((t) => t.transaction.id).includes(itemId),
    [transactionsSelected]
  );

  const headers: HeaderType<TransactionResponseTypeResult>[] = [
    {
      key: "transaction.transactionCode",
      label: t("transaction"),
      testId: "transaction",
      col: 3,
    },
    {
      key: "transaction.orderNumber",
      label: t("orderNumber"),
      testId: "order-number",
      textConfig: {
        formatter: (order) => maxlengthColumns(order),
      },
      col: 3,
    },
    {
      key: "products[0].sku",
      label: 'SKU',
      testId: "sku",
      textConfig: {
        formatter: (sku) => sku ? maxlengthColumns(sku) : formatNoValue(sku),
      },
      col: 3,
    },
    {
      key: "transaction.externalPickerId",
      label: t("pickerId"),
      testId: "picker-id",
      textConfig: {
        formatter: (pickerId) => formatNoValue(pickerId),
      },
      col: 3,
    },
    {
      key: "trip.id",
      label: t("trip"),
      testId: "trip-id",
      textConfig: {
        formatter: (tripId) => formatNoValue(tripId),
      },
      col: 3,
    },

    {
      key: ["origin.street", "origin.streetNumber"],
      label: t("origin"),
      testId: "origin",
      col: 4,
    },
    {
      key: "transaction.createdAt",
      label: t("dateAndHour"),
      testId: "sla-expiration-date",
      textConfig: {
        formatter: (date) => `${formatDDMMYYYY(date)} - ${formatHHMM(date)}`,
      },
    },
    {
      key: "transaction.state.tag",
      label: t("status") || "Estado",
      testId: "state",
      type: "tag",
      tagConfig: {
        variations: {
          [TRANSACTION_STATUS.PENDING_ASSIGNMENT]: {
            label: t("tableTransactions:pendingAssignment"),
            status: "error",
          },
          [TRANSACTION_STATUS.IN_PICK_UP]: {
            label: t("tableTransactions:inPickUp"),
            status: "warning",
          },
          [TRANSACTION_STATUS.ASSIGNED]: {
            label: t("tableTransactions:assigned"),
            status: "warning",
          },
          [TRANSACTION_STATUS.IN_PICK_UP_POINT]: {
            label: t("tableTransactions:inPickUpPoint"),
            status: "warning",
          },
          [TRANSACTION_STATUS.IN_DELIVERY]: {
            label: t("tableTransactions:inDelivery"),
            status: "success",
          },
          [TRANSACTION_STATUS.IN_DELIVERY_POINT]: {
            label: t("tableTransactions:inDeliveryPoint"),
            status: "success",
          },
          [TRANSACTION_STATUS.DELIVERED]: {
            label: t("tableTransactions:delivered"),
            status: "disabled",
          },
          [TRANSACTION_STATUS.IN_RETURN_TO_SENDER]: {
            label: t("tableTransactions:inReturnToSender"),
            status: "success",
          },
          [TRANSACTION_STATUS.RETURNED_TO_SENDER]: {
            label: t("tableTransactions:returnedToSender"),
            status: "disabled",
          },
          [TRANSACTION_STATUS.LOST]: {
            label: t("tableTransactions:lost"),
            status: "disabled",
          },
          [TRANSACTION_STATUS.CANCEL]: {
            label: t("tableTransactions:cancel"),
            status: "disabled",
          },
        },
      },
      col: 3,
    },
    {
      type: "checkbox",
      col: 1,
      testId: "select",
      align: "center",
      showCondition: (item: TransactionResponseTypeResult) =>
        item.transaction.state.tag === "PENDING_ASSIGNMENT",
      checkboxConfig: {
        isChecked: (item) => !!isSelected(item.transaction.id),
        onChangeCheck: (item) => toggleItem && toggleItem(item),
        selectAll: () => toggleAll && toggleAll(),
        allSelected: () => !!isAllSelected,
      },
    },
  ];

  return (
    <Table<TransactionResponseTypeResult>
      data={transactions}
      headers={headers}
      testId={testId}
      rowCount={rowCount}
      pagination="view-more-button"
      hasMoreRows={seeMore}
      fontSize={fontSize}
      cols={26}
      onClickRow={(item) => getDetailTransaction(item.transaction.id.toString())}
      loadMoreRows={() => getMoreTransactions({ ...filters, offset, limit })}
      dynamicHeight
      size="medium"
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  transactions: transactionsSelector(state).transactions,
  transactionsSelected: transactionsSelector(state).transactionsSelected,
  isAllSelected: allTransactionsSelected(state),
  filters: transactionsSelector(state).filters,
  limit: transactionsSelector(state).limit,
  offset: transactionsSelector(state).offset,
  collapse: transactionsSelector(state).collapseTable,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getDetailTransaction: (id: string) => {
    dispatch(detailTransaction.getDetailTransactionRequest(id));
  },
  toggleItem: (item: TransactionResponseTypeResult) => {
    dispatch(transactionActions.toggleItem(item));
  },
  toggleAll: () => {
    dispatch(transactionActions.toggleAll());
  },
  setCollapseTable: (collapse: boolean) => {
    dispatch(transactionActions.setCollapseTable(collapse));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(TableTransaction);
